import React from 'react';

export const TermsAndConditions: React.FC = () => {
    return (
        <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold text-gold mb-8">Terms & Conditions</h1>
            
            <div className="space-y-8 text-gray-300 leading-relaxed">
                <section>
                    <h2 className="text-2xl font-semibold text-gold mb-4">1. Introduction and Acceptance</h2>
                    <p className="mb-4">
                        These Terms and Conditions ("Terms") govern your use of the Aureus Alliance Holdings (Pty) Ltd website
                        (aureus.africa) and any share purchase transactions with Aureus Alliance Holdings (Pty) Ltd ("Company," "we," "our").
                    </p>
                    <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-4 mb-6">
                        <h3 className="text-lg font-semibold text-amber-400 mb-2">Important Notice</h3>
                        <p className="text-sm">
                            By accessing our website or purchasing shares, you acknowledge that you have read, understood,
                            and agree to be bound by these Terms and all applicable South African laws. If you do not agree
                            to these Terms, you must not use our website or purchase shares.
                        </p>
                    </div>
                    <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-blue-400 mb-2">Company Details</h3>
                        <p className="text-sm">
                            <strong>Company Name:</strong> Aureus Alliance Holdings (Pty) Ltd<br/>
                            <strong>Registration Number:</strong> 2025/368711/07<br/>
                            <strong>Registered Address:</strong> 1848 Mees Avenue, Randpark Ridge, Randburg, Gauteng, 2169<br/>
                            <strong>Office Address:</strong> 12 Gordon Road, La Lucia, Umhlanga, 4051, KZN, South Africa<br/>
                            <strong>Website:</strong> aureus.africa<br/>
                            <strong>Business:</strong> Gold mining operations and share ownership platform<br/>
                            <strong>Governing Law:</strong> Republic of South Africa
                        </p>
                    </div>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold text-gold mb-4">2. Share Ownership - Not Investment Products</h2>
                    <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 mb-6">
                        <h3 className="text-lg font-semibold text-green-400 mb-2">Important Clarification</h3>
                        <p className="text-sm mb-3">
                            The purchase of shares in Aureus Alliance Holdings (Pty) Ltd constitutes direct equity ownership
                            in a South African mining company, not the purchase of financial investment products, securities,
                            or collective investment schemes.
                        </p>
                        <p className="text-sm">
                            <strong>What You Are Purchasing:</strong> Direct ownership shares in a registered South African company
                            that owns and operates gold mining concessions.
                        </p>
                    </div>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold text-gold mb-4">3. Share Purchase Terms</h2>
                    <div className="space-y-4">
                        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                            <h3 className="text-lg font-semibold text-blue-400 mb-2">📋 CIPC Share Certificate Processing</h3>
                            <ul className="list-disc list-inside space-y-1 text-sm">
                                <li><strong>Processing Time:</strong> 10-14 business days after purchase completion</li>
                                <li><strong>KYC Requirement:</strong> Must be completed and approved before certificate issuance</li>
                                <li><strong>Issuing Authority:</strong> CIPC (Companies and Intellectual Property Commission)</li>
                                <li><strong>Digital Access:</strong> Available through your dashboard upon completion</li>
                                <li><strong>Physical Certificates:</strong> Available upon request for additional fee</li>
                            </ul>
                        </div>
                        <p>All share purchases are subject to current phase pricing. Payment must be completed before allocation.</p>
                    </div>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold text-gold mb-4">4. Financial Disclaimers</h2>
                    <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 space-y-3">
                        <h3 className="text-yellow-400 font-bold">⚠️ NOT FINANCIAL ADVICE</h3>
                        <div className="space-y-2 text-sm">
                            <p><strong>The Company does not provide financial advice.</strong> All information is for informational purposes only.</p>
                            <p><strong>One share will not make you rich.</strong> Consider shares as part of diversified strategy.</p>
                            <p><strong>Not a replacement for traditional financial products:</strong></p>
                            <ul className="list-disc list-inside ml-4 space-y-1">
                                <li>Life insurance policies</li>
                                <li>Funeral insurance or burial schemes</li>
                                <li>Pension funds or retirement annuities</li>
                                <li>Medical aid or health insurance</li>
                                <li>Government grants or social benefits</li>
                                <li>Emergency savings or liquid investments</li>
                            </ul>
                            <p><strong>Consult qualified professionals</strong> before buying shares.</p>
                        </div>
                    </div>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold text-gold mb-4">5. Risk Disclosure</h2>
                    <div className="space-y-4">
                        <p><strong>Share Purchase Risk Warning:</strong> Share purchases carry significant risk. Share values may fluctuate, and you may lose some or all of the amount you paid for your shares.</p>

                        <h3 className="text-lg font-semibold text-white mt-6 mb-3">Mining Operation Risks</h3>
                        <p>Gold mining operations involve various operational risks including:</p>
                        <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                            <li>Equipment failure, maintenance costs, and replacement expenses</li>
                            <li>Production delays due to weather conditions, technical problems, or supply chain disruptions</li>
                            <li>Safety incidents, mining accidents, and operational shutdowns</li>
                            <li>Labor disputes, strikes, wage negotiations, and skills shortages</li>
                            <li>Power supply issues including load shedding and energy cost increases</li>
                            <li>Water availability challenges, drought conditions, and processing limitations</li>
                            <li>Ore grade variations and lower than expected gold content</li>
                            <li>Geological surprises including unexpected rock formations and ground instability</li>
                        </ul>

                        <h3 className="text-lg font-semibold text-white mt-6 mb-3">Regulatory and Legal Risks</h3>
                        <p>The Company's operations are subject to various regulatory and legal risks:</p>
                        <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                            <li>Mining license changes, permit renewals, and regulatory compliance requirements</li>
                            <li>Environmental regulations and stricter compliance requirements</li>
                            <li>Taxation changes including mining royalties, corporate tax rates, and export duties</li>
                            <li>Land rights disputes, community claims, and traditional land rights</li>
                            <li>Government policy changes including nationalization risks and foreign ownership restrictions</li>
                        </ul>

                        <h3 className="text-lg font-semibold text-white mt-6 mb-3">Market and Economic Risks</h3>
                        <p>Market conditions and economic factors may affect the Company's performance:</p>
                        <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                            <li>Gold price volatility and fluctuations in international gold prices</li>
                            <li>Currency fluctuations, particularly USD/ZAR exchange rate impacts</li>
                            <li>Inflation impacts on operational costs including fuel, electricity, labor, and materials</li>
                            <li>Economic downturns affecting demand, financing, and market stability</li>
                            <li>Commodity market cycles and boom-bust cycles affecting mining profitability</li>
                        </ul>

                        <h3 className="text-lg font-semibold text-white mt-6 mb-3">Company-Specific Risks</h3>
                        <p>Specific risks related to the Company include:</p>
                        <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                            <li>Early-stage operations with limited operating history</li>
                            <li>Management dependency and reliance on key personnel</li>
                            <li>Capital requirements and need for ongoing share capital</li>
                            <li>Limited liquidity - shares may not be easily sold or transferred</li>
                            <li>No guarantee of a secondary market for share resale</li>
                        </ul>
                    </div>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold text-gold mb-4">6. Dividend Distribution Policy</h2>
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-white mt-6 mb-3">Profit Sharing Guarantee</h3>
                        <p>All shareholders are guaranteed to receive their proportional share of company profits generated from mining operations and other business ventures. However, the amount of dividends will vary based on actual company performance and profitability.</p>

                        <h3 className="text-lg font-semibold text-white mt-6 mb-3">Dividend Calculation Basis</h3>
                        <p>Dividend payments are calculated and distributed as follows:</p>
                        <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                            <li>Dividends are calculated according to actual company profit after all operational expenses</li>
                            <li>Profits are generated from resources actually mined and sold, plus other business ventures</li>
                            <li>Each shareholder receives dividends proportional to their shareholding percentage</li>
                            <li>Dividend amounts will vary based on company performance - there are no guaranteed percentage-based returns</li>
                            <li>Dividend declarations and timing are at the discretion of the Board of Directors</li>
                        </ul>

                        <h3 className="text-lg font-semibold text-white mt-6 mb-3">Factors Affecting Dividend Amounts</h3>
                        <p>The amount of dividends distributed will depend on various factors including:</p>
                        <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                            <li>Mining production volumes and actual gold extracted</li>
                            <li>Gold market prices at the time of sale</li>
                            <li>Operational costs including mining, processing, labor, and overhead expenses</li>
                            <li>Capital expenditures for equipment, facilities, and expansion projects</li>
                            <li>Regulatory compliance costs for environmental, safety, and legal requirements</li>
                            <li>Debt servicing including loan repayments and interest expenses</li>
                            <li>Tax obligations and other statutory requirements</li>
                            <li>Reserves maintained for future operations and business development</li>
                        </ul>

                        <h3 className="text-lg font-semibold text-white mt-6 mb-3">Important Disclaimers</h3>
                        <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                            <li><strong>No guaranteed percentage returns:</strong> While shareholders are guaranteed to receive profits, there are no guaranteed percentage-based returns on share ownership</li>
                            <li><strong>Variable dividend amounts:</strong> Dividend payments will vary based on actual business performance and may be small during challenging periods</li>
                            <li><strong>Projection estimates:</strong> Any dividend projections or estimates provided are for illustrative purposes only and actual results may differ significantly</li>
                            <li><strong>Business performance dependency:</strong> All dividend payments are dependent on the Company generating positive cash flows and profits</li>
                        </ul>
                    </div>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold text-gold mb-4">7. Limitation of Liability</h2>
                    <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4 space-y-3">
                        <h3 className="text-gray-300 font-bold">MAXIMUM LIABILITY LIMITATION</h3>
                        <div className="space-y-2 text-sm">
                            <p><strong>Aureus Alliance Holdings (Pty) Ltd cannot be held accountable for:</strong></p>
                            <ul className="list-disc list-inside ml-4 space-y-1">
                                <li>Financial losses from share ownership or trading</li>
                                <li>Death, injury, or personal harm not directly caused by Company negligence</li>
                                <li>Business interruption or loss of profits</li>
                                <li>Data loss or system failures</li>
                                <li>Third-party actions or market conditions</li>
                                <li>Force majeure events (natural disasters, war, government actions)</li>
                                <li>Cryptocurrency or payment processing failures</li>
                                <li>Tax implications or changes in tax law</li>
                            </ul>
                            <p><strong>Maximum liability:</strong> Total liability shall not exceed amount paid by User for shares.</p>
                        </div>
                    </div>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold text-gold mb-4">8. User Obligations</h2>
                    <div className="space-y-3">
                        <p>Users must:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm ml-4">
                            <li>Be at least 18 years old</li>
                            <li>Provide accurate information during registration and KYC</li>
                            <li>Maintain account security and confidentiality</li>
                            <li>Comply with all applicable laws</li>
                            <li>Not engage in fraudulent or illegal activities</li>
                        </ul>
                    </div>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold text-gold mb-4">9. Privacy and Data Protection</h2>
                    <p>Personal information is processed according to our Privacy Policy and POPIA compliance.</p>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold text-gold mb-4">10. Termination</h2>
                    <p>The Company may terminate accounts for violations. Users may close accounts subject to outstanding obligations.</p>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold text-gold mb-4">11. Governing Law</h2>
                    <p>These Terms are governed by South African law. Disputes subject to South African court jurisdiction.</p>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold text-gold mb-4">12. Contact Information</h2>
                    <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4">
                        <p><strong>Aureus Alliance Holdings (Pty) Ltd</strong></p>
                        <p>Email: info@aureus.africa</p>
                        <p>Support: support@aureus.africa</p>
                        <p>WhatsApp: JP Rademeyer +27 78 369 9799</p>
                        <p>WhatsApp: Donovan James +27 74 449 3251</p>
                        <p>Registered Address: 1848 Mees Avenue, Randpark Ridge, Randburg, Gauteng, 2169</p>
                        <p>Office Address: 12 Gordon Road, La Lucia, Umhlanga, 4051, KZN, South Africa</p>
                        <p>Website: aureus.africa</p>
                    </div>
                </section>

                <section>
                    <h2 className="text-2xl font-semibold text-gold mb-4">13. Shareholder Disclosure &amp; Regulatory Status Statement</h2>
                    <p className="mb-4">
                        The following statement is included for accuracy and transparency. It records a clarification of the
                        share structure, together with the due-diligence information below.
                    </p>

                    <h3 className="text-lg font-semibold text-white mt-6 mb-3">1. CIPC registration and FSCA / FSP licensing status</h3>
                    <h4 className="text-base font-semibold text-gold mb-2">Company registration</h4>
                    <p className="mb-4">
                        Aureus Alliance Holdings (Pty) Ltd is duly incorporated as a private company under the Companies Act 71 of 2008.
                    </p>
                    <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-6">
                        <p className="text-sm">
                            <strong>Enterprise Name:</strong> AUREUS ALLIANCE HOLDINGS<br/>
                            <strong>CIPC Registration Number:</strong> 2025/368711/07<br/>
                            <strong>Alternative CIPC reference (MOI system reference):</strong> K2025368711<br/>
                            <strong>Registered Office:</strong> 1848 Mees Avenue, Randpark Ridge, Randburg, Gauteng, 2169<br/>
                            <strong>Office Address:</strong> 12 Gordon Road, La Lucia, Umhlanga, 4051, KZN, South Africa
                        </p>
                    </div>

                    <h4 className="text-base font-semibold text-gold mb-2">FSCA / FSP position</h4>
                    <p className="mb-4">
                        Aureus Alliance Holdings (Pty) Ltd is not an investment company and does not issue financial products or
                        provide financial services. The company issues its own ordinary shares as equity in the business. Shareholders
                        participate in profits only through dividends derived from the company’s operational income, including the
                        extraction, refinement and sale of mineral resources.
                    </p>
                    <p className="mb-4">
                        In law, this constitutes a company issuing shares that entitle holders to participate in profits. This activity
                        is regulated by the Companies Act 71 of 2008, and by corporate governance and disclosure obligations, and not by
                        the financial-services licensing regime administered by the FSCA.
                    </p>
                    <p className="mb-3">The FSCA licenses only entities that provide financial services to others, including:</p>
                    <div className="overflow-x-auto mb-6">
                        <table className="w-full text-sm border border-gray-600">
                            <thead>
                                <tr className="bg-gray-800">
                                    <th className="text-left p-3 border-b border-gray-600 text-white font-semibold">FSCA-regulated activity</th>
                                    <th className="text-left p-3 border-b border-gray-600 text-white font-semibold">Aureus Alliance Holdings</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="p-3 border-b border-gray-700">Giving investment advice</td>
                                    <td className="p-3 border-b border-gray-700">No</td>
                                </tr>
                                <tr>
                                    <td className="p-3 border-b border-gray-700">Managing client money or portfolios</td>
                                    <td className="p-3 border-b border-gray-700">No</td>
                                </tr>
                                <tr>
                                    <td className="p-3 border-b border-gray-700">Selling or distributing third-party financial products</td>
                                    <td className="p-3 border-b border-gray-700">No</td>
                                </tr>
                                <tr>
                                    <td className="p-3 border-b border-gray-700">Operating pooled investment vehicles</td>
                                    <td className="p-3 border-b border-gray-700">No</td>
                                </tr>
                                <tr>
                                    <td className="p-3 border-b border-gray-700">Acting as a broker or intermediary</td>
                                    <td className="p-3 border-b border-gray-700">No</td>
                                </tr>
                                <tr>
                                    <td className="p-3">Issuing its own company’s shares</td>
                                    <td className="p-3">Permitted without an FSCA licence</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p className="mb-4">
                        The company is not advising persons on what to invest in, not managing portfolios, and not selling securities
                        issued by other entities. It offers ownership in itself: if a person acquires shares, they become a shareholder,
                        and if the company earns profits, dividends may be declared. This is governed by company law, not financial-services law.
                    </p>
                    <p className="mb-4"><strong>Accordingly, no FSCA or FSP licence is required.</strong></p>

                    <h4 className="text-base font-semibold text-gold mb-2">Where FSCA oversight may apply (disclosure, not licensing)</h4>
                    <p className="mb-3">
                        FSCA involvement arises only if the company makes a public offer of shares or lists securities on a regulated
                        market, in which case it must comply with:
                    </p>
                    <ul className="list-disc list-inside ml-4 space-y-1 text-sm mb-4">
                        <li>prospectus and disclosure requirements under the Companies Act, and</li>
                        <li>market-conduct rules under the Financial Markets Act.</li>
                    </ul>
                    <p className="mb-4">
                        This is a disclosure regime, not a licensing regime. A private placement of shares does not trigger these public-offer requirements.
                    </p>

                    <h4 className="text-base font-semibold text-gold mb-2">Legal character of the company</h4>
                    <p className="mb-4">
                        Aureus Alliance Holdings (Pty) Ltd is a dividend-paying equity company. This is the same structure used by mining,
                        oil and gas, property, manufacturing and technology companies: they sell shares, operate their business, generate
                        profits and distribute dividends.
                    </p>
                    <div className="overflow-x-auto mb-6">
                        <table className="w-full text-sm border border-gray-600">
                            <thead>
                                <tr className="bg-gray-800">
                                    <th className="text-left p-3 border-b border-gray-600 text-white font-semibold">Equity company</th>
                                    <th className="text-left p-3 border-b border-gray-600 text-white font-semibold">Investment scheme</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="p-3 border-b border-gray-700">You buy part of the company</td>
                                    <td className="p-3 border-b border-gray-700">You give money to be managed</td>
                                </tr>
                                <tr>
                                    <td className="p-3 border-b border-gray-700">You own shares</td>
                                    <td className="p-3 border-b border-gray-700">You own a claim</td>
                                </tr>
                                <tr>
                                    <td className="p-3 border-b border-gray-700">You receive dividends</td>
                                    <td className="p-3 border-b border-gray-700">You receive returns</td>
                                </tr>
                                <tr>
                                    <td className="p-3 border-b border-gray-700">Company runs its own business</td>
                                    <td className="p-3 border-b border-gray-700">Someone invests on your behalf</td>
                                </tr>
                                <tr>
                                    <td className="p-3">Companies Act</td>
                                    <td className="p-3">FSCA / FAIS / CIS</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p className="mb-4">Aureus Alliance Holdings operates entirely on the equity company side of this distinction.</p>
                    <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 mb-6">
                        <h4 className="text-green-400 font-semibold mb-2">Bottom line</h4>
                        <p className="text-sm">
                            Aureus Alliance Holdings (Pty) Ltd is a private company issuing dividend-paying shares backed by real business
                            operations. It is not an investment company, not a financial service, does not require FSCA licensing, and is
                            governed by the Companies Act.
                        </p>
                    </div>

                    <h3 className="text-lg font-semibold text-white mt-6 mb-3">2. Legal nature of shares and shareholder rights</h3>
                    <p className="mb-3">The company is authorised to issue <strong>3,000,000 ordinary shares</strong>, structured as follows.</p>
                    <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-4">
                        <h4 className="text-blue-400 font-semibold mb-2">Public dividend-earning shares</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm">
                            <li>1,400,000 shares</li>
                            <li>Sold across 20 phases (from $5 per share up to $1,000)</li>
                            <li>Expected to complete no later than July 2027</li>
                            <li>Availability of shares cannot be guaranteed. Depending on demand, they may be fully subscribed within a matter of months.</li>
                        </ul>
                        <p className="text-sm mt-3">
                            Recent sales data shows an upward trajectory across phases, including increased enquiries from larger private
                            and corporate participants. However, timing remains dependent on market demand.
                        </p>
                        <p className="text-sm mt-3"><strong>Rights attached:</strong></p>
                        <ul className="list-disc list-inside space-y-1 text-sm">
                            <li>1 vote per share</li>
                            <li>Dividends when and if declared</li>
                            <li>Participation in residual assets after creditors on liquidation</li>
                        </ul>
                    </div>
                    <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4 mb-6">
                        <h4 className="text-white font-semibold mb-2">Company-held shares</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm">
                            <li>1,600,000 shares</li>
                            <li>Held internally</li>
                        </ul>
                    </div>

                    <h3 className="text-lg font-semibold text-white mt-6 mb-3">3. Use and protection of shareholder funds</h3>
                    <p className="mb-3">Funds paid for shares are:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1 text-sm mb-4">
                        <li>not placed in escrow</li>
                        <li>not separated into custodial accounts</li>
                    </ul>
                    <p className="mb-4">They become working capital of the company.</p>
                    <h4 className="text-base font-semibold text-gold mb-2">How funds are applied</h4>
                    <p className="mb-4">
                        Funds raised are not used exclusively for the Kadoma mining operation, but may also be applied toward the
                        exploration of additional mining opportunities and other business initiatives that contribute to the company’s
                        turnover and profitability, which in turn contributes to shareholders’ increased share value and dividends.
                    </p>
                    <p className="mb-3">This includes, where appropriate:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1 text-sm mb-4">
                        <li>mining development and expansion</li>
                        <li>exploration and feasibility activities</li>
                        <li>strategic business opportunities aligned with company growth</li>
                    </ul>
                    <p className="mb-4">As with all private equity capital, funds deployed remain capital at risk.</p>

                    <h3 className="text-lg font-semibold text-white mt-6 mb-3">4. Assets backing the value of shares, and whether audited</h3>
                    <p className="mb-3">The intention is that share value will be supported by:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1 text-sm mb-4">
                        <li>the mining operation in Kadoma</li>
                        <li>the mining equipment and wash infrastructure</li>
                        <li>future production of gold and other minerals</li>
                    </ul>
                    <p className="mb-3">However, at this stage:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1 text-sm mb-4">
                        <li>there are no independently audited reserve valuations yet</li>
                        <li>the mine is still approaching production launch</li>
                    </ul>
                    <p className="mb-4">Independent valuations and audits will follow as operations progress.</p>

                    <h3 className="text-lg font-semibold text-white mt-6 mb-3">5. Availability of audited financial statements</h3>
                    <p className="mb-4">
                        There are no audited operational financial statements yet, as production has not fully commenced.
                        Audited financial reporting will follow once there is sustained operating history.
                    </p>

                    <h3 className="text-lg font-semibold text-white mt-6 mb-3">6. Dividends — calculation, projections, and track record</h3>
                    <h4 className="text-base font-semibold text-gold mb-2">Dividend basis</h4>
                    <p className="mb-4">
                        If and when dividends are declared, they are calculated as net distributable profit divided equally over the shares.
                    </p>
                    <h4 className="text-base font-semibold text-gold mb-2">Earlier projections (not the payout)</h4>
                    <p className="mb-3">
                        An earlier internal operational model estimated the following. These figures are not the dividend payout:
                    </p>
                    <ul className="list-disc list-inside ml-4 space-y-1 text-sm mb-4">
                        <li>±100 kg of gold per month</li>
                        <li>±1,200 kg projected for 2026</li>
                        <li>Indicative modelling of approximately $60 per share for 2026</li>
                        <li>A first potential dividend window of April 2026</li>
                    </ul>
                    <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 mb-4">
                        <h4 className="text-yellow-400 font-semibold mb-2">Position as of October 2026</h4>
                        <p className="text-sm mb-3">
                            The company is nearly in 2027. The dividend payout cannot be what that earlier model projected.
                            The plants are behind schedule. Excessive floods delayed the work, and new EMA protocol rules in 2026
                            delayed it a further 3 months. As of October 2026, the company is only in testing of the non-alluvial material.
                        </p>
                        <p className="text-sm">
                            The dividend position has been adjusted to what is currently on the land and can be safely processed.
                        </p>
                    </div>
                    <p className="mb-3"><strong>Key realities:</strong></p>
                    <ul className="list-disc list-inside ml-4 space-y-1 text-sm mb-4">
                        <li>startup ramp-up may be slower</li>
                        <li>plant commissioning phases affect output</li>
                        <li>excessive floods have delayed the plants</li>
                        <li>new EMA protocol rules in 2026 added a further 3 months of delay</li>
                        <li>as of October 2026, work is limited to testing of the non-alluvial material</li>
                        <li>gold prices fluctuate</li>
                        <li>dividends depend on actual profits and Board approval</li>
                        <li>any dividend must follow what is currently on the land and can be safely processed</li>
                    </ul>
                    <h4 className="text-base font-semibold text-gold mb-2">Track record</h4>
                    <p className="mb-4">There is currently no historical dividend track record, as operations are new.</p>

                    <h3 className="text-lg font-semibold text-white mt-6 mb-3">7. Liquidity and exit options</h3>
                    <p className="mb-4">Aureus is currently a private, unlisted company.</p>
                    <p className="mb-3">Liquidity framework:</p>
                    <ol className="list-decimal list-inside ml-4 space-y-1 text-sm mb-4">
                        <li>Sale of all 1,400,000 dividend shares</li>
                        <li>Tokenisation into NFT share certificates</li>
                        <li>Listing on the Aureus Marketplace</li>
                        <li>Shareholders may sell their shares there</li>
                    </ol>
                    <p className="mb-4">
                        Because shares are par-value USD $1,000, the marketplace framework anticipates a minimum listing value of $1,000 per share.
                    </p>
                    <ul className="list-disc list-inside ml-4 space-y-1 text-sm mb-4">
                        <li>liquidity is not guaranteed</li>
                        <li>resale depends on demand</li>
                        <li>the company does not guarantee any buyer or resale price</li>
                    </ul>
                    <p className="mb-4">Until then, transfers occur via internal MOI processes.</p>

                    <h3 className="text-lg font-semibold text-white mt-6 mb-3">8. Key risks and shareholder protections</h3>
                    <p className="mb-3">It is important that all shareholders fully understand and accept the risks:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1 text-sm mb-4">
                        <li>startup and operational risk</li>
                        <li>possible delays from funding or weather</li>
                        <li>production levels may be lower than projected</li>
                        <li>gold price volatility affects revenue</li>
                        <li>capital is not escrow-protected</li>
                        <li>although strong uptake in share sales is anticipated, sales may also progress slower than expected</li>
                        <li>regulatory changes may occur in South Africa or Zimbabwe</li>
                    </ul>
                    <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 mb-6">
                        <p className="text-sm">
                            Independent due diligence and professional legal and financial advice are strongly recommended.
                        </p>
                    </div>

                    <h3 className="text-lg font-semibold text-white mt-6 mb-3">Confidentiality</h3>
                    <p className="mb-4">
                        Documents shared privately for review are strictly confidential and should not be distributed without the
                        Company’s written consent.
                    </p>
                    <p className="mb-4">
                        This is not speculative exposure. It is equity in real gold production, aligned with sustainable development
                        and generational impact.
                    </p>
                    <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4">
                        <p className="mb-2"><strong>Issued by Aureus Alliance Holdings Leadership</strong></p>
                        <p>Donovan James, CEO of Aureus Alliance Holdings</p>
                        <p>Jean-Pierre Rademeyer, CEO of Smart United Network (SUN)</p>
                    </div>
                </section>

                <div className="border-t border-gray-600 pt-6 mt-8">
                    <p className="text-sm text-white">
                        Last Updated: {new Date().toLocaleDateString()}<br/>
                        These Terms are effective as of the date last updated.
                    </p>
                </div>
            </div>
        </div>
    );
};
